import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ConfigGeneral from "./ConfigGeneral";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import { BRAND, WHATSAPP_GREEN } from "./theme/brand";
import ConfigEtapas from "./ConfigEtapas";
import ConfigVendedores from "./ConfigVendedores";
import ConfigPlantillasWA from "./ConfigPlantillasWA";

const SECTIONS = [
  { key: "general", label: "General", icon: <SettingsIcon sx={{ fontSize: 20 }} />, color: BRAND.navy },
  { key: "etapas", label: "Etapas del pipeline", icon: <ViewKanbanOutlinedIcon sx={{ fontSize: 20 }} />, color: BRAND.bright },
  { key: "vendedores", label: "Vendedores", icon: <PeopleIcon sx={{ fontSize: 20 }} />, color: "#3A76A3" },
  { key: "plantillas", label: "Plantillas de WhatsApp", icon: <WhatsAppIcon sx={{ fontSize: 20 }} />, color: WHATSAPP_GREEN },
];

function getBadge(key, store) {
  if (key === "vendedores") return store.config.vendedores.length || null;
  if (key === "plantillas") return (store.config.plantillasWA || []).length || null;
  return null;
}

export default function ConfigCoreBase({ open, onClose, store }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? "100%" : "90vh" } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          bgcolor: "navy.main",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
        }}
      >
        Configuración del negocio
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }} aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
        {SECTIONS.map(({ key, label, icon, color }) => {
          const badge = getBadge(key, store);
          return (
            <Accordion
              key={key}
              defaultExpanded={key === "general"}
              disableGutters
              elevation={0}
              data-onboarding-id={key === "plantillas" ? "onb-config-plantillas" : undefined}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: "12px !important",
                mb: 1.5,
                "&::before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 48,
                  px: 2,
                  "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.25, my: 0.75 },
                }}
              >
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: `${color}18`, color, flexShrink: 0 }}
                >
                  {icon}
                </Stack>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                  {label}
                </Typography>
                {badge && (
                  <Chip label={badge} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: `${color}18`, color }} />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                {key === "general" && <ConfigGeneral store={store} />}
                {key === "etapas" && <ConfigEtapas store={store} />}
                {key === "vendedores" && <ConfigVendedores store={store} />}
                {key === "plantillas" && <ConfigPlantillasWA store={store} />}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Button
          onClick={onClose}
          variant="contained"
          startIcon={<CheckCircleIcon />}
          fullWidth={isMobile}
          sx={{ py: isMobile ? 1.2 : undefined, fontWeight: 700 }}
        >
          Listo
        </Button>
      </DialogActions>
    </Dialog>
  );
}
